import { useState, useEffect } from 'react';
import { useAgents } from '../../hooks/useAgents';
import { useCreateSchedule, useUpdateSchedule, useSchedule } from '../../hooks/useSchedules';
import { Button, Input, Modal, Select, Textarea, Label } from '@/components/ui';
import { TimezoneCombobox } from '@/components/ui/TimezoneCombobox';
import { useToast } from '../../components/ui/Toast';
import type { Schedule, CreateScheduleInput, UpdateScheduleInput, ScheduleType } from '../../types';

interface CreateScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId?: string;
  schedule?: Schedule;
}

const RECURRING_PRESETS = [
  { value: 'rate(1 hour)', label: 'Every hour' },
  { value: 'cron(0 0 * * ? *)', label: 'Daily at midnight' },
  { value: 'cron(0 9 ? * MON-FRI *)', label: 'Weekdays at 9am' },
  { value: 'custom', label: 'Custom expression' },
];

export default function CreateScheduleModal({
  isOpen,
  onClose,
  agentId: propAgentId,
  schedule,
}: CreateScheduleModalProps) {
  const { success, error: showError } = useToast();
  const { data: agentsData } = useAgents();
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();

  // Fetch full schedule details (list API may omit large fields like instructions)
  const { data: fullSchedule } = useSchedule(schedule?.scheduleId);
  const resolvedSchedule = fullSchedule ?? schedule;

  const isEdit = !!schedule;
  const agents = agentsData?.items ?? [];

  const [agentId, setAgentId] = useState(propAgentId || '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('recurring');
  const [oneTimeDate, setOneTimeDate] = useState('');
  const [recurringPreset, setRecurringPreset] = useState('rate(1 hour)');
  const [customExpression, setCustomExpression] = useState('');
  const [instructions, setInstructions] = useState('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [maxExecutions, setMaxExecutions] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (resolvedSchedule) {
        setAgentId(resolvedSchedule.agentId);
        setName(resolvedSchedule.name);
        setDescription(resolvedSchedule.description || '');
        setScheduleType(resolvedSchedule.scheduleType);
        setInstructions(resolvedSchedule.instructions || '');
        setTimezone(resolvedSchedule.timezone);
        setMaxExecutions(resolvedSchedule.maxExecutions?.toString() || '');

        if (resolvedSchedule.scheduleType === 'one_time') {
          setOneTimeDate(resolvedSchedule.scheduleExpression);
        } else {
          const preset = RECURRING_PRESETS.find(
            (p) => p.value === resolvedSchedule.scheduleExpression,
          );
          if (preset) {
            setRecurringPreset(resolvedSchedule.scheduleExpression);
            setCustomExpression('');
          } else {
            setRecurringPreset('custom');
            setCustomExpression(resolvedSchedule.scheduleExpression);
          }
        }
      } else if (!schedule) {
        setAgentId(propAgentId || '');
        setName('');
        setDescription('');
        setScheduleType('recurring');
        setOneTimeDate('');
        setRecurringPreset('rate(1 hour)');
        setCustomExpression('');
        setInstructions('');
        setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        setMaxExecutions('');
      }
    }
  }, [isOpen, resolvedSchedule, schedule, propAgentId]);

  const getScheduleExpression = (): string => {
    if (scheduleType === 'one_time') {
      return new Date(oneTimeDate).toISOString();
    }
    return recurringPreset === 'custom' ? customExpression : recurringPreset;
  };

  const isValid = (): boolean => {
    if (!agentId || !name.trim() || !instructions.trim()) return false;
    if (scheduleType === 'one_time') {
      if (!oneTimeDate) return false;
      if (new Date(oneTimeDate) <= new Date()) return false;
    } else {
      if (recurringPreset === 'custom' && !customExpression.trim()) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    try {
      if (isEdit) {
        const input: UpdateScheduleInput = {
          name,
          description: description || undefined,
          instructions,
          scheduleExpression: getScheduleExpression(),
          timezone,
          maxExecutions: maxExecutions ? parseInt(maxExecutions, 10) : undefined,
        };
        await updateSchedule.mutateAsync({
          scheduleId: schedule!.scheduleId,
          input,
        });
        success('Schedule updated successfully');
      } else {
        const input: CreateScheduleInput = {
          agentId,
          name,
          description: description || undefined,
          instructions,
          scheduleType,
          scheduleExpression: getScheduleExpression(),
          timezone,
          maxExecutions: maxExecutions ? parseInt(maxExecutions, 10) : undefined,
        };
        await createSchedule.mutateAsync(input);
        success('Schedule created successfully');
      }
      onClose();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} schedule`,
      );
    }
  };

  const isPending = createSchedule.isPending || updateSchedule.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Schedule' : 'Create Schedule'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isPending} disabled={!isValid()}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Agent select */}
        <Select
          label="Agent"
          placeholder="Select an agent..."
          options={agents.map((a) => ({ value: a.agentId, label: a.name }))}
          value={agentId}
          onChange={(e: { target: { value: string } }) => setAgentId(e.target.value)}
          disabled={!!propAgentId || isEdit}
        />

        {/* Name */}
        <Input
          label="Name"
          placeholder="Daily report generation"
          value={name}
          onChange={(e: { target: { value: string } }) => setName(e.target.value)}
        />

        {/* Description */}
        <Input
          label="Description"
          placeholder="Optional description"
          value={description}
          onChange={(e: { target: { value: string } }) => setDescription(e.target.value)}
        />

        {/* Schedule Type */}
        <Select
          label="Schedule Type"
          options={[
            { value: 'one_time', label: 'One-time' },
            { value: 'recurring', label: 'Recurring' },
          ]}
          value={scheduleType}
          onChange={(e: { target: { value: string } }) =>
            setScheduleType(e.target.value as ScheduleType)
          }
          disabled={isEdit}
        />

        {/* Schedule Expression */}
        {scheduleType === 'one_time' ? (
          <div>
            <Label className="mb-1 block">Date & Time</Label>
            <input
              type="datetime-local"
              value={oneTimeDate}
              onChange={(e) => setOneTimeDate(e.target.value)}
              className="focus-ring flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Select
              label="Frequency"
              options={RECURRING_PRESETS}
              value={recurringPreset}
              onChange={(e: { target: { value: string } }) =>
                setRecurringPreset(e.target.value)
              }
            />
            {recurringPreset === 'custom' && (
              <Input
                label="Custom Expression"
                placeholder="cron(0 12 * * ? *) or rate(5 minutes)"
                value={customExpression}
                onChange={(e: { target: { value: string } }) =>
                  setCustomExpression(e.target.value)
                }
                hint="AWS cron or rate expression"
              />
            )}
          </div>
        )}

        {/* Instructions */}
        <div>
          <Label className="mb-1 block">Instructions</Label>
          <Textarea
            placeholder="What should the agent do when this schedule triggers?"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={4}
          />
        </div>

        {/* Timezone */}
        <div>
          <Label className="mb-1 block">Timezone</Label>
          <TimezoneCombobox value={timezone} onChange={setTimezone} />
        </div>

        {/* Max Executions (recurring only) */}
        {scheduleType === 'recurring' && (
          <Input
            label="Max Executions"
            type="number"
            placeholder="Unlimited"
            value={maxExecutions}
            onChange={(e: { target: { value: string } }) =>
              setMaxExecutions(e.target.value)
            }
            hint="Leave empty for unlimited"
          />
        )}
      </div>
    </Modal>
  );
}
